// @ts-nocheck
import * as React from 'react';
// InternalLayout — Shell for internal AI Teacher pages.
// Shared left rail + top bar + page router + tweaks panel.

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import useTweaks from '../hooks/useTweaks';
import ScalingFrame from '../components/layout/ScalingFrame';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import Toasts from '../components/feedback/Toasts';
import DemoTriggerModal from '../components/feedback/DemoTriggerModal';
import { fetchWhatsAppDemoStatus, requestWhatsAppDemoApprovalQr, sendParentEngageDemoMessage, sendWhatsAppDemo } from '../console/services/whatsappDemoApi';
import TweaksPanel from '../components/tweaks/TweaksPanel';
import { TweakSection, TweakToggle } from '../components/tweaks/TweakControls';
import Avatar from '../components/common/Avatar';
import Icon from '../components/common/Icon';
import { useAdmissionsLeads } from '../context/AdmissionsLeadsContext';
import InspirePage from './pages/InspirePage';
import ChildcarePage from './pages/ChildcarePage';
import StudentInfoPage from './pages/StudentInfoPage';
import MultilingualPage from './pages/MultilingualPage';
import FeedbackPage from './pages/FeedbackPage';
import HelpdeskPage from './pages/HelpdeskPage';
import './InternalLayout.css';

const PAGE_META = {
  inspire:   { Component: InspirePage,      accent: "#7c3aed", title: "Inspire 365" },
  childcare: { Component: ChildcarePage,    accent: "#db2777", title: "AI Child Care" },
  progress:  { Component: StudentInfoPage,  accent: "#1769ff", title: "AI Student Information" },
  language:  { Component: MultilingualPage, accent: "#0891b2", title: "Multilingual Communication" },
  feedback:  { Component: FeedbackPage,     accent: "#16a34a", title: "AI Feedback & Surveys" },
  helpdesk:  { Component: HelpdeskPage,     accent: "#b45309", title: "AI Parental Helpdesk" },
};

const TWEAK_DEFAULTS = {
  trust: true,
};

export default function InternalLayout() {
  const { pageId } = useParams();
  const authData = useAuth();
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const {
    leads,
    loading: leadsLoading,
    error: leadsError,
    selectedLeadId,
    setSelectedLeadId,
    selectedLead,
    ensureLeadDetails,
  } = useAdmissionsLeads();
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [pendingDemo, setPendingDemo] = useState(null);
  const [selectedDemoContent, setSelectedDemoContent] = useState(null);
  const [studentMenuOpen, setStudentMenuOpen] = useState(false);
  const [isWhatsAppConfirmed, setIsWhatsAppConfirmed] = useState(false);
  const [directApprovalQr, setDirectApprovalQr] = useState(null);
  const studentMenuRef = useRef(null);

  const page = PAGE_META[pageId] ? pageId : "inspire";

  useEffect(() => { setSelectedDemoContent(null); }, [page, selectedLeadId]);

  useEffect(() => {
    const closeMenu = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'mousedown' && studentMenuRef.current?.contains(event.target)) return;
      setStudentMenuOpen(false);
    };
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeMenu);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeMenu);
    };
  }, []);

  useEffect(() => {
    if (!selectedLead || selectedLead.detailsLoaded) return;
    let current = true;
    setDetailsLoading(true);
    setDetailsError('');
    ensureLeadDetails(selectedLead.id)
      .catch((requestError) => {
        if (current) setDetailsError(requestError?.response?.data?.message || requestError?.message || 'Unable to load student details.');
      })
      .finally(() => { if (current) setDetailsLoading(false); });
    return () => { current = false; };
  }, [selectedLead, ensureLeadDetails]);

  const student = selectedLead;

  // Toasts
  const [toasts, setToasts] = useState([]);
  const sendToParent = async (label, generatedContent = null, options = {}) => {
    if (['inspire', 'childcare', 'progress'].includes(page)) {
      setPendingDemo({ id: page, name: label, icon: PAGE_META[page].Component === InspirePage ? 'sparkles' : page === 'childcare' ? 'parent' : 'student', color: meta.accent, tint: `color-mix(in srgb, ${meta.accent} 10%, transparent)`, generatedContent: generatedContent || {} });
      return;
    }
    const id = `t-${Date.now()}`;
    try {
      const status = await fetchWhatsAppDemoStatus(student?.phone);
      if (!status?.data?.confirmed) {
        const qr = await requestWhatsAppDemoApprovalQr({ mobile: student?.phone });
        setDirectApprovalQr(qr);
        return;
      }
      const feature = { language: 'multilingual', feedback: 'survey', helpdesk: 'helpdesk' }[page];
      const languageCode = ({ English: 'en', Hindi: 'hi', Telugu: 'te' })[options.language || student?.language] || 'en';
      const response = await sendParentEngageDemoMessage({ mobile: student?.phone, feature, languageCode });
      setToasts((arr) => [...arr, {
        id,
        title: response?.message || `${label} sent to ${student?.name || 'parent'}`,
        sub: `Delivered on WhatsApp · ${student?.phone || ''}${student?.language ? ` · in ${student.language}` : ''}`,
      }]);
    } catch (error) {
      setToasts((arr) => [...arr, {
        id,
        title: 'Unable to send on WhatsApp',
        sub: error?.response?.data?.message || error?.message || 'The parent must tap Approve Demo to open the 24-hour window.',
      }]);
    }
    setTimeout(() => setToasts((arr) => arr.filter((x) => x.id !== id)), 4600);
  };

  const meta = PAGE_META[page];
  const PageComp = meta.Component;
  const requestDemoApproval = ({ phone }) => requestWhatsAppDemoApprovalQr({ mobile: phone });
  const checkDemoStatus = async ({ phone }) => {
    const status = await fetchWhatsAppDemoStatus(phone);
    const confirmed = Boolean(status?.data?.confirmed);
    setIsWhatsAppConfirmed(confirmed);
    return confirmed;
  };
  const sendDemo = async ({ phone, lang, note, assetTypes }) => {
    const content = pendingDemo?.generatedContent || {};
    const info = authData?.USER_INFO?.[0] || {};
    const filename = (url = '') => { try { return decodeURIComponent(new URL(url).pathname.split('/').pop() || ''); } catch { return ''; } };
    const common = {
      MOBILE: phone,
      CAPTION: content.TITLE || content.HEADING || pendingDemo?.name || '',
      BODY: note || content.MESSAGE || content.ISSUE || '',
      CALLBACK_DATA: JSON.stringify({ lead_id: student?.leadId, feature_id: page }),
      INSTITUTE_ID: Number(authData?.INSTITUTE_ID || info.INSTITUTE_ID || 0),
      BRANCH_ID: Number(authData?.BRANCH_ID || info.BRANCH_ID || 0),
      DATABASE: authData?.DATABASE || info.DATABASE || '',
      USER_ID: Number(authData?.USER_ID || info.USER_ID || 0),
    };
    const response = await sendWhatsAppDemo(assetTypes.map((assetType) => {
      const type = assetType.toLowerCase();
      const link = type === 'video' ? content.VIDEO_URL : type === 'audio' ? content.AUDIO_URL : '';
      return { ...common, TYPE: type, LINK: link || '', FILENAME: filename(link) };
    }));
    const toastId = `t-${Date.now()}`;
    setToasts((current) => [...current, {
      id: toastId,
      title: response?.message || `${pendingDemo?.name || meta.title} sent to ${student?.name || 'parent'}`,
      sub: `${assetTypes.join(', ')} · WhatsApp ${phone}${lang ? ` · in ${lang}` : ''}`,
    }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== toastId)), 4600);
    setPendingDemo(null);
  };

  return (
    <div className="frame-shell">
      <ScalingFrame>
        <Sidebar activeId={page} />
        <TopBar
          crumbTitle={meta.title}
          rightSlot={(
            <div className="topbar__actions">
              <div className="ctx-student" ref={studentMenuRef}>
                <button className={`ctx-chip${studentMenuOpen ? ' is-open' : ''}`} type="button" aria-haspopup="listbox" aria-expanded={studentMenuOpen} disabled={leadsLoading || leads.length === 0} onClick={() => setStudentMenuOpen((open) => !open)}>
                  <span className="ctx-chip__dot" />
                  {student ? <Avatar p={student} size={30} /> : null}
                  <span className="ctx-chip__meta">
                    <span className="ctx-chip__name">{student?.child || (leadsLoading ? 'Loading students…' : 'No students found')}</span>
                    <span className="ctx-chip__sub">{student ? `${student.grade} · Age ${student.childAge}${student.gender ? ` · ${student.gender}` : ''}` : (leadsError || 'Admissions leads')}</span>
                  </span>
                  <svg className="ctx-chip__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
                </button>
                {studentMenuOpen ? <div className="ctx-menu" role="listbox" aria-label="Students">
                  <div className="ctx-menu__head"><span>Select student</span><small>{leads.length} leads</small></div>
                  <div className="ctx-menu__list">
                    {leads.map((lead) => {
                      const active = lead.id === selectedLeadId;
                      return <button key={lead.id} type="button" role="option" aria-selected={active} className={`ctx-menu__option${active ? ' is-selected' : ''}`} onClick={() => { setDetailsError(''); setSelectedLeadId(lead.id); setStudentMenuOpen(false); }}>
                        <Avatar p={lead} size={34} />
                        <span className="ctx-menu__option-copy"><strong>{lead.child}</strong><small>{lead.grade} · Age {lead.childAge}{lead.gender ? ` · ${lead.gender}` : ''}</small></span>
                        <span className="ctx-menu__check">{active ? '✓' : ''}</span>
                      </button>;
                    })}
                  </div>
                </div>
                : null}
              </div>
              {!['language', 'feedback', 'helpdesk'].includes(page) && <button className="cta-pill cta-pill--wa" disabled={!student || detailsLoading || !selectedDemoContent} onClick={() => sendToParent(meta.title, selectedDemoContent)} title="Send selected content to the parent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3.5A11 11 0 0 0 3.6 17.6L2 22l4.5-1.5A11 11 0 1 0 20.5 3.5z"/></svg>
                Send to parent
              </button>}
            </div>
          )}
        />

        <div className={"work work--scroll" + (t.trust ? "" : " hide-trust")}>
          {student?.detailsLoaded ? <PageComp key={`${page}-${student.id}`} student={student} accent={meta.accent} sendToParent={sendToParent} onContentSelect={setSelectedDemoContent} /> : (
            detailsError || leadsError
              ? <div className="internal-loading" role="alert">{detailsError || leadsError}</div>
              : <InternalPageSkeleton />
          )}
        </div>
        <Toasts items={toasts} onDismiss={(id) => setToasts((arr) => arr.filter((x) => x.id !== id))} />
        <DemoTriggerModal open={Boolean(pendingDemo)} parent={student} pillar={pendingDemo} onClose={() => setPendingDemo(null)} onRequestDemo={requestDemoApproval} onCheckStatus={checkDemoStatus} isWhatsAppConfirmed={isWhatsAppConfirmed} onConfirm={sendDemo} />
        {directApprovalQr && <div className="demo-qr-layer" onClick={() => setDirectApprovalQr(null)}><div className="demo-qr-dialog" role="dialog" aria-modal="true" aria-labelledby="direct-demo-qr-title" onClick={(event) => event.stopPropagation()}><button className="demo-qr-dialog__close" type="button" onClick={() => setDirectApprovalQr(null)} aria-label="Close QR code">×</button><div className="demo-qr-dialog__icon"><Icon name="comms" size={20} /></div><div className="demo-approval-qr__copy"><strong id="direct-demo-qr-title">Parent approval required</strong><span>Ask the parent to scan this QR code or open the WhatsApp link, then try sending again.</span></div>{directApprovalQr.qrImage ? <img className="demo-approval-qr__image" src={directApprovalQr.qrImage} alt="WhatsApp parent approval QR code" /> : null}{directApprovalQr.token ? <span className="demo-qr-dialog__reference">Reference: {directApprovalQr.token}</span> : null}{directApprovalQr.expiresInSeconds ? <span className="demo-qr-dialog__expiry">Valid for {Math.round(directApprovalQr.expiresInSeconds / 3600)} hours</span> : null}{directApprovalQr.waLink ? <a className="demo-approval-qr__link" href={directApprovalQr.waLink} target="_blank" rel="noreferrer">Open approval in WhatsApp</a> : null}</div></div>}
      </ScalingFrame>

      <TweaksPanel>
        <TweakSection label="Demo presentation" />
        <TweakToggle label="Show trust & accreditation logos" value={t.trust} onChange={(v) => setTweak("trust", v)} />
      </TweaksPanel>
    </div>
  );
}

function InternalPageSkeleton() {
  return <div className="ip ip-page-skeleton" role="status" aria-label="Loading personalised student experience">
    <div className="ip-page-skeleton__hero">
      <div className="ip-page-skeleton__copy">
        <span className="ip-skeleton ip-skeleton--eyebrow" />
        <span className="ip-skeleton ip-skeleton--title" />
        <span className="ip-skeleton ip-skeleton--title ip-skeleton--title-short" />
        <span className="ip-skeleton ip-skeleton--line" />
        <span className="ip-skeleton ip-skeleton--line ip-skeleton--line-short" />
        <span className="ip-skeleton ip-skeleton--button" />
      </div>
      <span className="ip-skeleton ip-page-skeleton__feature" />
    </div>
    <div className="ip-page-skeleton__section">
      <span className="ip-skeleton ip-skeleton--eyebrow" />
      <span className="ip-skeleton ip-skeleton--section-title" />
      <div className="ip-page-skeleton__grid">
        {Array.from({ length: 6 }).map((_, index) => <span key={index} className="ip-skeleton ip-page-skeleton__card" />)}
      </div>
    </div>
  </div>;
}
